import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, timeout, finalize } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../app.config';
import { LoadingService } from '../shared/loading.service';

export interface PhoneVerificationRequest {
    phone: string;
    requestType: string;
    source: string;
}

export interface PhoneVerificationResponse {
    statusCode?: number;
    headers?: { 'Content-Type': string };
    body?: {
        phone: string;
        phonetype: string;
        isvalid: boolean;
        issmscapable: boolean;
        source: string;
    };
    // Legacy fields — kept for safety
    success?: boolean;
    error?: string;
    details?: string;
}

export interface SendSMSRequest {
    SourceSystem: string;
    MessageTemplate: string;
    PhoneNumber: string;
    MessageFooter: string;
    TemplateVariables: {
        TV_PolicyNumber: string;
        TV_DocumentType: string;
        TV_DocumentLink: {
            GetDataFrom: string;
            attachmentDocId: string;
        };
    };
}

export interface SendSMSResponse {
    success: boolean;
    message?: string;
    error?: string;
    data?: {
        success: boolean;
        messageId: string;
    };
}

export interface SendPaymentLinkSMSRequest {
    SourceSystem: string;
    MessageTemplate: string;
    PhoneNumber: string;
    MessageFooter: string;
    TemplateVariables: {
        TV_PolicyNumber: string;
        TV_PaymentLink: string;
    };
}

export interface SendPortletLinkSMSRequest {
    SourceSystem: string;
    MessageTemplate: string;
    PhoneNumber: string;
    MessageFooter: string;
    TemplateVariables: {
        TV_PolicyNumber: string;
        TV_PaymentLink: string;
    };
}

export interface SendThirdPartyLinkSMSRequest {
    SourceSystem: string;
    MessageTemplate: string;
    PhoneNumber: string;
    MessageFooter: string;
    TemplateVariables: {
        TV_ThirdPartyLink: {
            thirdPartyURL: string;
        };
    };
}

export interface SendDocumentUploadLinkSMSRequest {
    SourceSystem: string;
    MessageTemplate: string;
    PhoneNumber: string;
    MessageFooter: string;
    TemplateVariables: {
        TV_PolicyNumber: string;
        TV_DocumentPortletLink: string;
    };
}

export interface SendSNLLinkSMSRequest {
    SourceSystem: string;
    MessageTemplate: string;
    PhoneNumber: string;
    MessageFooter: string;
    TemplateVariables: {
        TV_PolicyNumber: string;
        TV_SNLPortletLink: string;
    };
}

@Injectable({
    providedIn: 'root',
})
export class PhoneService {
    private readonly TIMEOUT = 20000;
    private readonly headers = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    constructor(
        private http: HttpClient,
        private loader: LoadingService,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {}

    validatePhone(phoneNumber: string): Observable<any> {
        if (!phoneNumber || phoneNumber.length !== 10) {
            return of({ phoneService: true, message: 'Phone number must be 10 digits' });
        }

        const request: PhoneVerificationRequest = {
            phone: phoneNumber,
            requestType: 'verifyPhoneNumber',
            source: 'ADHOC',
        };

        const url = this.config.verifyContactUrl;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-api-key': this.config.verifyContactApiKey,
        });

        return this.http.post<PhoneVerificationResponse>(url, request, { headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                if (this.isValidPhone(res)) {
                    return null;
                }
                return {
                    phoneService: true,
                    message: this.getErrorMessage(res),
                };
            }),
            catchError((error) => {
                console.error('Phone verification error:', error);
                return of({
                    phoneService: true,
                    message: 'Unable to verify phone number. Please try again.',
                });
            })
        );
    }

    private getErrorMessage(res: PhoneVerificationResponse): string {
        if (res.body?.isvalid === false) {
            return 'This phone number is invalid. Please double-check it.';
        }
        return 'This phone number is invalid. Please double-check it.';
    }

    sendSMS(
        phoneNumber: string,
        policyNumber: string,
        documentType: string,
        attachmentDocId: string
    ): Observable<SendSMSResponse> {
        const request: SendSMSRequest = {
            SourceSystem: 'POLICY360_ADHOC',
            MessageTemplate: 'SMS_Link_Document_Download',
            PhoneNumber: phoneNumber,
            MessageFooter: 'N',
            TemplateVariables: {
                TV_PolicyNumber: policyNumber,
                TV_DocumentType: documentType,
                TV_DocumentLink: {
                    GetDataFrom: 'DocumentHub',
                    attachmentDocId: attachmentDocId,
                },
            },
        };

        const url = this.config.smsOrchestratorURL;

        this.loader.showLoading();

        return this.http.post<SendSMSResponse>(url, request, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                if (res.success && res.data?.success) {
                    return res;
                }
                throw new Error(res.error || res.message || 'Failed to send SMS');
            }),
            catchError((error) => {
                console.error('SMS send error:', error);
                return throwError(() => error);
            }),
            finalize(() => this.loader.hideLoading())
        );
    }

    sendPaymentLinkSMS(
        phoneNumber: string,
        policyNumber: string,
        paymentLink: string,
        zipcode: string
    ): Observable<SendSMSResponse> {
        const dynamicPaymentLink = this.buildPaymentUrl(paymentLink, policyNumber, zipcode);
        const request: SendPaymentLinkSMSRequest = {
            SourceSystem: 'POLICY360_ADHOC',
            MessageTemplate: 'SMS_Payment_Link',
            PhoneNumber: phoneNumber,
            MessageFooter: 'N',
            TemplateVariables: {
                TV_PolicyNumber: policyNumber,
                TV_PaymentLink: dynamicPaymentLink,
            },
        };

        this.loader.showLoading();

        return this.http.post<SendSMSResponse>(this.config.smsOrchestratorURL, request, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                if (res.success && res.data?.success) {
                    return res;
                }
                throw new Error(res.error || res.message || 'Failed to send payment link SMS');
            }),
            catchError((error) => {
                console.error('Payment link SMS send error:', error);
                return throwError(() => error);
            }),
            finalize(() => this.loader.hideLoading())
        );
    }

    sendPortletLinkSMS(phoneNumber: string, policyNumber: string, portletLink: string): Observable<SendSMSResponse> {
        const request: SendPortletLinkSMSRequest = {
            SourceSystem: 'POLICY360_ADHOC',
            MessageTemplate: 'SMS_Payment_Link',
            PhoneNumber: phoneNumber,
            MessageFooter: 'N',
            TemplateVariables: {
                TV_PolicyNumber: policyNumber,
                TV_PaymentLink: portletLink,
            },
        };

        this.loader.showLoading();

        return this.http.post<SendSMSResponse>(this.config.smsOrchestratorURL, request, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                if (res.success && res.data?.success) {
                    return res;
                }
                throw new Error(res.error || res.message || 'Failed to send portlet link SMS');
            }),
            catchError((error) => {
                console.error('Portlet link SMS send error:', error);
                return throwError(() => error);
            }),
            finalize(() => this.loader.hideLoading())
        );
    }

    sendThirdPartyLinkSMS(phoneNumber: string, thirdPartyLoginURL: string): Observable<SendSMSResponse> {
        const request: SendThirdPartyLinkSMSRequest = {
            SourceSystem: 'POLICY360_ADHOC',
            MessageTemplate: 'SMS_Link_Third_Party_Policy360',
            PhoneNumber: phoneNumber,
            MessageFooter: 'N',
            TemplateVariables: {
                TV_ThirdPartyLink: {
                    thirdPartyURL: thirdPartyLoginURL,
                },
            },
        };

        this.loader.showLoading();

        return this.http.post<SendSMSResponse>(this.config.smsOrchestratorURL, request, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                const isSuccess = res.success !== false && res.data?.success !== false;
                if (isSuccess) {
                    return res;
                }
                throw new Error(res.error || 'Failed to send third party link SMS');
            }),
            catchError((error) => {
                console.error('Third party link SMS send error:', error);
                return throwError(() => error);
            }),
            finalize(() => this.loader.hideLoading())
        );
    }

    sendDocumentUploadLinkSMS(
        phoneNumber: string,
        policyNumber: string,
        documentUploadLink: string
    ): Observable<SendSMSResponse> {
        const request: SendDocumentUploadLinkSMSRequest = {
            SourceSystem: 'POLICY360_ADHOC',
            MessageTemplate: 'SMS_Link_Document_Upload',
            PhoneNumber: phoneNumber,
            MessageFooter: 'N',
            TemplateVariables: {
                TV_PolicyNumber: policyNumber,
                TV_DocumentPortletLink: documentUploadLink,
            },
        };

        this.loader.showLoading();

        return this.http.post<SendSMSResponse>(this.config.smsOrchestratorURL, request, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                if (res.success && res.data?.success) {
                    return res;
                }
                throw new Error(res.error || res.message || 'Failed to send document upload link SMS');
            }),
            catchError((error) => {
                console.error('Document upload link SMS send error:', error);
                return throwError(() => error);
            }),
            finalize(() => this.loader.hideLoading())
        );
    }
    sendSNLLinkSMS(phoneNumber: string, policyNumber: string, snlLink: string): Observable<SendSMSResponse> {
        const request: SendSNLLinkSMSRequest = {
            SourceSystem: 'POLICY360_ADHOC',
            MessageTemplate: 'SMS_Link_SNL',
            PhoneNumber: phoneNumber,
            MessageFooter: 'N',
            TemplateVariables: {
                TV_PolicyNumber: policyNumber,
                TV_SNLPortletLink: snlLink,
            },
        };

        this.loader.showLoading();

        return this.http.post<SendSMSResponse>(this.config.smsOrchestratorURL, request, { headers: this.headers }).pipe(
            timeout(this.TIMEOUT),
            map((res) => {
                if (res.success && res.data?.success) {
                    return res;
                }
                throw new Error(res.error || res.message || 'Failed to send SNL link SMS');
            }),
            catchError((error) => {
                console.error('SNL link SMS send error:', error);
                return throwError(() => error);
            }),
            finalize(() => this.loader.hideLoading())
        );
    }

    private isValidPhone(res: PhoneVerificationResponse): boolean {
        return res.body?.isvalid === true && res.body?.issmscapable === true;
    }

    private buildPaymentUrl(baseUrl: string, policyNumber: string, zipcode: string): string {
        return `${baseUrl}?Category=PolicyNumber&search=${policyNumber}&Zipcode=${zipcode}`;
    }
}
